import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { Toaster, toast } from "react-hot-toast";
import { TiDelete } from "react-icons/ti";
import DragAndDropButton from "../../Components/Buttons/DragAndDropButton";
import FormButton from "../../Components/Buttons/FormButton";
import FormToggle from "../../Components/FormTag/FormToggle";
import InputFiled from "../../Components/FormTag/InputFiled";
import {
  ALPHANUM_SPACE_REGEX,
  EMAIL,
  PHONE_USA
} from "../../Shared/Helper/Constant";
import { PATTERN_ERR_MSG, USA_PHONE_PATTERN_ERR_MSG } from "../../Shared/Helper/ValidationMessage";
import { BasicInfo, ClientData, attachments, ClientRequest } from "./model/Client.type";
import { submitClientForm, getClientDetails, updateClient } from "./service/client.service";
import { useNavigate, useParams } from "react-router-dom";
import { convertFileToBase64, formatPhoneNumber } from "../../Utility/Utility";

import Loader from "../../Components/GlobalLoader/Loader";
import FormSelect from "../../Components/FormTag/FormSelect";
import { lookUpService } from "../../Services/common/lookup.service";
import { SelcetOption } from "../../model/common/apiResponse";

interface BasicInfoProps {
  handelAllTabData?: (data: ClientData) => void;
  clientData?: ClientData;
  handleNext: () => void;
}

export default function ClientBasicInfo(props: BasicInfoProps) {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [isCreateMode, setIsCreateMode] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [countryList, setCountryList] = useState<any>();
  const navigate = useNavigate();
  const { handelAllTabData, clientData, handleNext } = props;
  const [basicInfo, setBasicInfo] = useState<BasicInfo>(
    new BasicInfo()
  );
  const {
    handleSubmit,
    register,
    setValue,
    getValues,
    control,
    clearErrors,
    trigger,
    reset,
    formState: { errors, isValid },
  } = useForm<BasicInfo>({
    defaultValues: { ...basicInfo, is_active: isCreateMode, is_used_for_packslip: isCreateMode },
    mode: "onChange",
  });
  const navigation = useNavigate();
  const [dataForLogo, setDataForLogo] = useState<File[]>([]);
  const [attachmentFiles, setAttachmentFiles] = useState<any[]>([]);

  const handleDeleteFileForLogo = (index: number) => {
    const newData = [...dataForLogo];
    newData.splice(index, 1);
    setDataForLogo(newData);
  };

  const validateAndClearErrors = async (fieldName: keyof BasicInfo) => {
    const isValid = await trigger(fieldName);
    if (isValid) {
      clearErrors(fieldName);
    }
  };
  
  const fetchData = async () => {
    try {
      const countryData = await lookUpService.countryLookup();
      const countryList: SelcetOption[] =
      countryData.data?.data?.map((obj: any) => ({
          label: obj.name,
          value: obj.code,
        }));
        setCountryList(countryList);
    } catch (error) {

    }
  };

  useEffect(() => {
    setLoading(true);
    fetchData().then(()=>{
      if (id) {
        getClientDetails(id).then((response) => {
          const data = response?.data;
          if (data) {
            data.country=data.country.code;
            reset(data);
          }
        }).finally(() => {
          setLoading(false);
        });
      } else {
        setLoading(false);
      }
    });

  }, [id, setValue]);


  const nextData: SubmitHandler<BasicInfo> = async (data) => {
    if (submitting) return;
    setSubmitting(true);

    const requestData: ClientRequest = {
      ...data,
      contact_phone: data.contact_phone,
    };
    if(data.country){
      requestData.country={
        code: data.country,
      };
    }else{
      requestData.country={};
    }

    const logoObject = await finalAttachment(dataForLogo);
    if ((logoObject).length > 0) {
      requestData.logo = logoObject[0];
    }
    try {

      let response;
      if (id) {
        if (requestData.logo && requestData.logo.url) {
          delete requestData.logo;
        }
        response = await updateClient(requestData, parseInt(id));
      } else {
        response = await submitClientForm(requestData);
      }

      if (response.status == 201 || response.status == 200 ) {
        const operationType = id? 'updated':'created';
        toast.success("Client "+operationType+" successfully!", { duration: 2000 });
        navigation("/account/client/overview/grid-view");
      } else if (response.status == 400) {
        if (response.error.detail.code) {
          toast.error(response.error.detail.code, { duration: 2000 });
        } else if (response.error.detail.country_code) {
          toast.error(response.error.detail.country_code, { duration: 2000 });
        }
        setSubmitting(false);
      } else {
        toast.error("An error occurred while submitting Client.", {
          duration: 2000,
        });
        setSubmitting(false);
      }
    } catch (error) {
      toast.error("An error occurred while submitting Client.", {
        duration: 2000,
      });
      setSubmitting(false);
    } finally {
      setSubmitting(false);
    }
  };

  const finalAttachment = async (dataN: File[]): Promise<attachments[]> => {
    const attachmentsArray = await Promise.all(
      dataN?.map(async (file, index) => {
        const existingAttachment = attachmentFiles[index];
        if (existingAttachment?.temporaryUrl) {
          return {
            original_name: existingAttachment.original_name,
            description: existingAttachment.description,
            content: existingAttachment.temporaryUrl,
            id: existingAttachment.id
          };
        }
        const base64Data = await convertFileToBase64(file);
        return {
          original_name: file.name,
          description: file.name,
          content: base64Data
        };
      }) ?? []
    );
    return attachmentsArray;
  };

  const handelCancelBtn = () => {
    reset(new BasicInfo());
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <>
      <Toaster />
      <div className="my-2 bg-white rounded p-3">
        <form>
          <div className="d-flex px-2 gap-3">
            <div className="col-4 px-2">
              <label className="fw-medium">Client Code *</label>
              <div className="mt-1">
                <InputFiled
                  tabIndex={1}
                  autoFocus={true}
                  type="text"
                  register={register}
                  name="code"
                  errors={errors.code}
                  className="form-control"
                  placeholder="Enter Code"
                  required={true}
                  onChange={async (e: any) => {
                    await validateAndClearErrors("code");
                    console.log(errors);
                  }}
                />
              </div>
            </div>
            <div className="col-4 px-2">
              <label className="fw-medium">Client Name *</label>
              <div className="mt-1">
                <InputFiled
                  tabIndex={2}
                  type="text"
                  register={register}
                  name="name"
                  errors={errors.name}
                  className="form-control"
                  placeholder="Enter Name"
                  required={true}
                  onChange={async (e: any) => {
                    await validateAndClearErrors("name");
                  }}
                />
              </div>
            </div>
            <div className="col-3 d-flex px-2 justify-content-center">
              <div className="ms-4 mt-1">
                <label className="fw-medium">Active</label>
              </div>
              <div className="form-check form-switch sidebar-toggler">
                <div className="mx-2 mt-1">
                  <FormToggle
                    register={register}
                    name="is_active"
                    tabIndex={13}
                    defaultChecked={getValues("is_active") || setValue("is_active", false)}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="d-flex px-2 mt-3 gap-3">
            <div className="col-4 px-2">
              <label className="fw-medium">Phone</label>
              <div className="mt-1">
                <InputFiled
                  tabIndex={3}
                  maxLength={14}
                  type="text"
                  register={register}
                  name="contact_phone"
                  errors={errors.contact_phone}
                  pattern={{
                    type: PHONE_USA,
                    message: USA_PHONE_PATTERN_ERR_MSG,
                  }}
                  className="form-control"
                  placeholder="000-000-0000"
                  onChange={async (e: any) => {
                    const formattedNumber = formatPhoneNumber(e.target.value);
                    setValue("contact_phone", formattedNumber);
                    await validateAndClearErrors("contact_phone");
                  }}
                />
              </div>
            </div>
            <div className="col-4 px-2">
              <label className="fw-medium">Email</label>
              <div className="mt-1">
                <InputFiled
                  tabIndex={4}
                  type="text"
                  register={register}
                  name="contact_email"
                  errors={errors.contact_email}
                  pattern={{ type: EMAIL, message: PATTERN_ERR_MSG }}
                  className="form-control"
                  placeholder="Enter Email"
                  onChange={async (e: any) => {
                    await validateAndClearErrors("contact_email");
                  }}
                />
              </div>
            </div>
          </div>
          <div className="d-flex px-2 mt-3 gap-3">
            <div className="col-4 px-2">
              <label className="fw-medium">Address 1</label>
              <div className="mt-2">
                <InputFiled
                  tabIndex={5}
                  type="text"
                  register={register}
                  name="address_line_1"
                  errors={errors.address_line_1}
                  pattern={{
                    type: ALPHANUM_SPACE_REGEX,
                    message: PATTERN_ERR_MSG,
                  }}
                  className="form-control"
                  placeholder="Enter Address 1"
                  onChange={async (e: any) => {
                    await validateAndClearErrors("address_line_1");
                  }}
                />
              </div>
            </div>
            <div className="col-4 px-2">
              <label className="fw-medium">Address 2</label>
              <div className="mt-1">
                <InputFiled
                  tabIndex={6}
                  type="text"
                  register={register}
                  name="address_line_2"
                  errors={errors.address_line_2}
                  pattern={{
                    type: ALPHANUM_SPACE_REGEX,
                    message: PATTERN_ERR_MSG,
                  }}
                  className="form-control"
                  placeholder="Enter Address 2"
                  onChange={async (e: any) => {
                    await validateAndClearErrors("address_line_2");
                  }}
                />
              </div>
            </div>
          </div>
          <div className="d-flex px-2 mt-3 gap-3">
            <div className="col-4 px-2">
              <label className="fw-medium">Country</label>
              <div className="mt-1">
                {/* <InputFiled
                  type="text"
                  tabIndex={7}
                  name="country_code"
                  register={register}
                  className="form-control"
                  placeholder="Select Country"
                  errors={errors.country_code}
                  onChange={async (e: any) => {
                    await validateAndClearErrors("country_code");
                  }}
                /> */}
                  <FormSelect
                    name="country"
                    autofocus={true}
                    tabIndex={7}
                    control={control}
                    optionList={countryList?? []}
                    className="form-control p-0 "
                    placeholder="Select"
                    isSearchable={true}
                  />
              </div>
            </div>
            <div className="col-4 px-2">
              <label className="fw-medium">State/Province</label>
              <div className="mt-1">
                <InputFiled
                  type="text"
                  tabIndex={8}
                  name="state_province"
                  register={register}
                  className="form-control"
                  placeholder="Enter State"
                  errors={errors.state_province}
                  onChange={async (e: any) => {
                    await validateAndClearErrors("state_province");
                  }}
                />
              </div>
            </div>
          </div>
          <div className="d-flex px-2 mt-3 gap-3">
            <div className="col-4 px-2">
              <label className="fw-medium">City</label>
              <div className="mt-1">
                <InputFiled
                  type="text"
                  tabIndex={9}
                  register={register}
                  name="city"
                  errors={errors.city}
                  pattern={{
                    type: ALPHANUM_SPACE_REGEX,
                    message: PATTERN_ERR_MSG,
                  }}
                  className="form-control"
                  placeholder="Enter City"
                  onChange={async (e: any) => {
                    await validateAndClearErrors("city");
                  }}
                />
              </div>
            </div>
            <div className="col-4 px-2">
              <label className="fw-medium">Zip/Postal Code</label>
              <div className="mt-1">
                <InputFiled
                  type="text"
                  tabIndex={10}
                  name="postal_code"
                  register={register}
                  className="form-control"
                  placeholder="Enter Postal Code"
                  onChange={async (e: any) => {
                    await validateAndClearErrors("postal_code");
                  }}
                />
              </div>
            </div>
          </div>
          <div className="d-flex px-2 mt-3 gap-3">
            <div className="col-4 px-2">
              <label className="fw-medium">Logo Upload</label>
              <div className="d-flex align-items-center mt-1 mb-2 gap-4">
                <DragAndDropButton
                  buttonName="Upload"
                  title="Import File"
                  buttonClassName="btn btn-color rounded-pill px-5"
                  dragAndDropModalMessage={`Drag and drop the file to import`}
                  acceptableFiles={["PNG", "JPG"]}
                  forCrop={true}
                  multiple={false}
                  data={setDataForLogo}
                />
              </div>
              {dataForLogo &&
                Object.values(dataForLogo)?.map((file, idx) => (
                  <div
                    key={idx}
                    className="mt-2 align-self-center text-truncate upload-msg-text fw-medium  clicked-option-with-background-and-cross"
                  >
                    {file.name}
                    <TiDelete onClick={() => handleDeleteFileForLogo(idx)} />
                  </div>
                ))}
            </div>
            
            <div className="col-4 d-flex px-2 pb-1 justify-content-between align-self-end">
              <div className="mt-1">
                <label className="fw-medium">Use this logo on pack slips</label>
              </div>
              <div className="d-flex justify-content-start mt-1">
                <div>
                  <FormToggle
                    register={register}
                    name="is_used_for_packslip"
                    tabIndex={12}
                    defaultChecked={getValues("is_used_for_packslip") || setValue("is_used_for_packslip", false)}
                  />
                </div>
                <div>Yes</div>
              </div>
            </div>
          </div>
          {(dataForLogo && Object.keys(dataForLogo)?.length===0 && getValues("logo")?.url) && (
                  <div className=" d-flex justify-content-start position-relative col-4 px-4 mt-2">
                    <a href={
                        getValues("logo") && getValues("logo").url
                          ? getValues("logo").url
                          : require("../../assets/images/noimage.png")
                      }
                      target="_blank"
                      >
                    <img
                      alt="Client Logo"
                      src={
                        getValues("logo") && getValues("logo").url
                          ? getValues("logo").url
                          : require("../../assets/images/noimage.png")
                      }
                      className="img-fluid rounded border"
                      style={{ maxWidth: "200px", maxHeight: "150px", objectFit: 'contain', height: "45px", width: "45px" }}

                    />
                    </a>
                  </div>
                )}
        </form>
      </div>

      <div className="my-2 bg-white rounded py-3">
        <div className="d-flex flex-row-reverse">
          <FormButton
            tabIndex={14}
            className="sph-btn me-4"
            handleClick={handleSubmit(nextData)}
          >
            {id? 'Update':'Submit'}
          </FormButton>
          <FormButton
            tabIndex={15}
            className="sph-btn sph-shift me-4"
            handleClick={handelCancelBtn}
          >
            Cancel
          </FormButton>
        </div>
      </div>
    </>
  );
}